import { useEffect, useState, type ReactNode } from 'react';
import { Collapse } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import type { DashboardDefensorResumoDto } from '@/api.types';
import {
  formatarDataPtBr,
  hrefFolhaHome,
  hrefRelatorioHome,
  rotuloTipoLotacao,
  rotuloTipoPeriodo,
  textoPeriodoVinculoHome,
  textoRelatorioHome,
} from '@/modules/home/application/defensor-home.utils';
import '@/modules/system/auth/presentation/pages/escolha-modulo.css';

type DefensorHomeResumoProps = {
  resumo: DashboardDefensorResumoDto | null;
  loading: boolean;
  erro: boolean;
};

type BlocoId = 'vinculos' | 'eventos' | 'folhas' | 'relatorios';

function blocosAbertosIniciais(resumo: DashboardDefensorResumoDto | null): Set<BlocoId> {
  const abertos = new Set<BlocoId>();
  if (!resumo) return abertos;
  if (resumo.vinculos.length > 0) abertos.add('vinculos');
  if (resumo.eventos.length > 0) abertos.add('eventos');
  if (resumo.folhas.length > 0) abertos.add('folhas');
  if (resumo.relatorios.length > 0) abertos.add('relatorios');
  return abertos;
}

export function DefensorHomeSaudacao({ resumo, loading }: Omit<DefensorHomeResumoProps, 'erro'>) {
  const nome = resumo?.nome?.trim();
  return (
    <h2 className="defensor-home-saudacao">
      {loading || !nome ? 'Olá.' : `Olá, ${nome}.`}
    </h2>
  );
}

function CardRecolhivel({
  id,
  titulo,
  quantidade,
  aberto,
  onToggle,
  vazio,
  possuiItens,
  children,
}: {
  id: string;
  titulo: string;
  quantidade: number;
  aberto: boolean;
  onToggle: () => void;
  vazio: string;
  possuiItens: boolean;
  children: ReactNode;
}) {
  const collapseId = `defensor-home-${id}`;

  return (
    <article className="card defensor-home-card">
      <button
        type="button"
        className="card-header defensor-home-card-header"
        onClick={onToggle}
        aria-expanded={aberto}
        aria-controls={collapseId}
      >
        <span className="defensor-home-card-titulo">
          <span className="defensor-home-card-chevron" aria-hidden>
            {aberto ? '▼' : '▶'}
          </span>
          {titulo}
        </span>
        <span className="badge bg-secondary">{quantidade}</span>
      </button>
      <Collapse in={aberto}>
        <div id={collapseId}>
          <div className="card-body defensor-home-card-body">
            {possuiItens ? (
              <ul className="defensor-home-lista">{children}</ul>
            ) : (
              <p className="text-muted mb-0">{vazio}</p>
            )}
          </div>
        </div>
      </Collapse>
    </article>
  );
}

export default function DefensorHomeResumo({ resumo, loading, erro }: DefensorHomeResumoProps) {
  const [abertos, setAbertos] = useState<Set<BlocoId>>(() => blocosAbertosIniciais(resumo));

  useEffect(() => {
    setAbertos(blocosAbertosIniciais(resumo));
  }, [resumo]);

  const alternar = (id: BlocoId) => {
    setAbertos((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(id)) proximo.delete(id);
      else proximo.add(id);
      return proximo;
    });
  };

  if (loading) {
    return (
      <div className="defensor-home-resumo">
        <p className="text-muted mb-0">Carregando suas informações...</p>
      </div>
    );
  }

  if (erro || !resumo) {
    return (
      <div className="defensor-home-resumo">
        <p className="text-muted mb-0">
          Não foi possível carregar suas informações agora. As áreas do sistema seguem disponíveis acima.
        </p>
      </div>
    );
  }

  return (
    <div className="defensor-home-resumo defensor-home-resumo--colunas">
      <CardRecolhivel
        id="vinculos"
        titulo="Seus vínculos ativos"
        quantidade={resumo.vinculos.length}
        aberto={abertos.has('vinculos')}
        onToggle={() => alternar('vinculos')}
        vazio="Nenhum vínculo vigente no momento."
        possuiItens={resumo.vinculos.length > 0}
      >
        {resumo.vinculos.map((vinculo, index) => {
          const periodo = textoPeriodoVinculoHome(vinculo.dataInicial, vinculo.dataFinal);
          return (
            <li key={`${vinculo.tipo}-${vinculo.unidade}-${index}`}>
              {rotuloTipoLotacao(vinculo.tipo)} na {vinculo.unidade}
              {periodo ? ` — ${periodo}` : ''}
            </li>
          );
        })}
      </CardRecolhivel>

      <CardRecolhivel
        id="eventos"
        titulo="Eventos vigentes"
        quantidade={resumo.eventos.length}
        aberto={abertos.has('eventos')}
        onToggle={() => alternar('eventos')}
        vazio="Nenhum evento vigente no momento."
        possuiItens={resumo.eventos.length > 0}
      >
        {resumo.eventos.map((evento, index) => (
          <li key={`${evento.tipo}-${evento.dataInicial}-${index}`}>
            {rotuloTipoPeriodo(evento.tipo)} no período de {formatarDataPtBr(evento.dataInicial)} a{' '}
            {formatarDataPtBr(evento.dataFinal)}.
          </li>
        ))}
      </CardRecolhivel>

      <CardRecolhivel
        id="folhas"
        titulo="Seus estagiários"
        quantidade={resumo.folhas.length}
        aberto={abertos.has('folhas')}
        onToggle={() => alternar('folhas')}
        vazio="Nenhuma folha aguardando sua assinatura."
        possuiItens={resumo.folhas.length > 0}
      >
        {resumo.folhas.map((folha) => (
          <li key={folha.folhaId}>
            <Link to={hrefFolhaHome(folha.folhaId)}>Folha para assinar de {folha.estagiarioNome}</Link>
          </li>
        ))}
      </CardRecolhivel>

      <CardRecolhivel
        id="relatorios"
        titulo="Seus relatórios"
        quantidade={resumo.relatorios.length}
        aberto={abertos.has('relatorios')}
        onToggle={() => alternar('relatorios')}
        vazio="Nenhum relatório pendente no momento."
        possuiItens={resumo.relatorios.length > 0}
      >
        {resumo.relatorios.map((item, index) => (
          <li key={`${item.mes}-${item.ano}-${item.situacao}-${item.relatorioId ?? index}`}>
            <Link to={hrefRelatorioHome(item)}>{textoRelatorioHome(item)}</Link>
          </li>
        ))}
      </CardRecolhivel>
    </div>
  );
}
